import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  DatePicker,
  Space,
  Table,
  Tag,
  Select,
  Typography,
  Tooltip,
  Button,
  Checkbox,
  Avatar,
  Statistic,
  Badge,
  Grid,
} from "antd";
import {
  PhoneOutlined,
  PhoneFilled,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  FilePdfOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../api/Api";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Legend, BarChart, Bar,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { FaFileCsv } from "react-icons/fa6";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;
const CheckboxGroup = Checkbox.Group;
const { useBreakpoint } = Grid;

const ListagemChamadas = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;

  const navigate = useNavigate();
  const inicioMes = dayjs().startOf("month");
  const fimMes = dayjs().endOf("month");
  const perfil = localStorage.getItem("perfil");

  const [datas, setDatas] = useState([inicioMes, fimMes]);
  const [loading, setLoading] = useState(false);
  const [chamadas, setChamadas] = useState([]);
  const [statusFiltro, setStatusFiltro] = useState(null);
  const [dadosChart, setDadosChart] = useState([]);
  const [tiposSelecionados, setTiposSelecionados] = useState(["Completada", "Sem_Resposta", "Abandonada"]);

  const [totalChamadas, setTotalChamadas] = useState(0);
  const [totalAtendidas, setTotalAtendidas] = useState(0);
  const [totalNaoAtendidas, setTotalNaoAtendidas] = useState(0);
  const [totalAbandonadas, setTotalAbandonadas] = useState(0);
  const [dadosAgente, setDadosAgente] = useState([]);
  const [filaFiltro, setFilaFiltro] = useState("");
  const [filasDisponiveis, setFilasDisponiveis] = useState([]);
  const [operadorFiltro, setOperadorFiltro] = useState("");
  const [operadoresDisponiveis, setOperadoresDisponiveis] = useState([]);

  useEffect(() => {
    if (datas && datas[0] && datas[1]) {
      fetchChamadas();
      fetchDashboard();
      fetchDadosChart();
      fetchDadosPorAgente();
    }
  }, [datas, filaFiltro]);

  const irParaGerarPDF = () => {
    const params = new URLSearchParams({ dataInicio: datas[0].format("YYYY-MM-DD"), dataFim: datas[1].format("YYYY-MM-DD") });
    window.open(`/chamadas_pdf?${params.toString()}`, "_blank");
  };

  const irParaGerarExcel = () => {
    const params = new URLSearchParams({ dataInicio: datas[0].format("YYYY-MM-DD"), dataFim: datas[1].format("YYYY-MM-DD"), statusFiltro: statusFiltro || "", filasSelecionadas: filaFiltro || "", operadorFiltro: operadorFiltro || "" });
    window.open(`/chamadas-excel?${params.toString()}`, "_blank");
  };

  const fetchDadosChart = async () => {
    try {
      const payload = { dataInicio: datas[0].format("YYYY-MM-DD"), dataFim: datas[1].format("YYYY-MM-DD"), fila: filaFiltro || null };
      const response = await api.ListChamadaPorDia(payload);
      if (Array.isArray(response)) {
        setDadosChart(response.map((item) => ({
          ...item,
          dia: /^\d{2}:\d{2}$/.test(item.dia) ? item.dia : dayjs(item.dia, ["DD/MM/YYYY", "YYYY-MM-DD"]).format("DD/MM"),
          Completada: parseInt(item.Completada) || 0,
          Sem_Resposta: parseInt(item.Sem_Resposta) || 0,
          Abandonada: parseInt(item.Abandonada) || 0,
        })));
      }
    } catch (error) { console.error("Erro ao buscar dados do chart:", error); }
  };

  const fetchDadosPorAgente = async () => {
    try {
      const payload = { dataInicio: datas[0].format("YYYY-MM-DD"), dataFim: datas[1].format("YYYY-MM-DD"), fila: filaFiltro || null };
      const response = await api.ListChamadaPorAgente(payload);
      if (Array.isArray(response)) setDadosAgente(response.map((item) => ({ agente: item.agente, total_atendidas: Number(item.total_atendidas) || 0 })));
    } catch (error) { console.error("Erro ao buscar dados por agente:", error); }
  };

  const fetchChamadas = async () => {
    try {
      setLoading(true);
      const payload = { dataInicio: datas[0].format("YYYY-MM-DD"), dataFim: datas[1].format("YYYY-MM-DD") };
      const response = await api.ListChamada(payload);
      if (Array.isArray(response)) {
        setChamadas(response);
        setFilasDisponiveis([...new Set(response.map((c) => c.fila).filter(Boolean))]);
        setOperadoresDisponiveis([...new Set(response.map((c) => c.agente).filter(Boolean))]);
      }
    } catch (error) { console.error("Erro ao buscar chamadas:", error); }
    finally { setLoading(false); }
  };

  const fetchDashboard = async () => {
    try {
      const payload = { dataInicio: datas[0].format("YYYY-MM-DD"), dataFim: datas[1].format("YYYY-MM-DD"), fila: filaFiltro || null };
      const response = await api.ListDashboard(payload);
      if (response && typeof response === "object") {
        setTotalChamadas(response.todas);
        setTotalAtendidas(response.atendidas);
        setTotalNaoAtendidas(response.n_atendidas);
        setTotalAbandonadas(response.abandonadas);
      }
    } catch (error) { console.error("Erro ao buscar dashboard:", error); }
  };

  const areasConfig = {
    Completada: { color: "#52c41a", gradientId: "colorCompletada", stroke: "#52c41a", name: "Completada" },
    Sem_Resposta: { color: "#ff4d4f", gradientId: "colorSemResposta", stroke: "#ff4d4f", name: "Sem resposta" },
    Abandonada: { color: "#fa8c16", gradientId: "colorAbandonada", stroke: "#fa8c16", name: "Abandonada" },
  };

  const filteredChamadas = chamadas.filter((c) => {
    const filtroStatusOk = !statusFiltro || c.status?.toLowerCase() === statusFiltro.toLowerCase();
    const filtroFilaOk = !filaFiltro || c.fila === filaFiltro;
    const filtroOperadorOk = !operadorFiltro || c.agente === operadorFiltro;
    return filtroStatusOk && filtroFilaOk && filtroOperadorOk;
  });

  const stats = useMemo(() => [
    { title: "Total de chamadas", value: totalChamadas, color: "#1890ff", icon: <PhoneFilled />, description: "Chamadas totais recebidas" },
    { title: "Atendidas", value: totalAtendidas, color: "#52c41a", icon: <CheckCircleOutlined />, description: "Chamadas completadas com sucesso" },
    { title: "Não atendidas", value: totalNaoAtendidas, color: "#ff4d4f", icon: <CloseCircleOutlined />, description: "Chamadas sem resposta" },
    { title: "Abandonadas", value: totalAbandonadas, color: "#fa8c16", icon: <ExclamationCircleOutlined />, description: "Chamadas abandonadas" },
  ], [totalChamadas, totalAtendidas, totalNaoAtendidas, totalAbandonadas]);

  const columns = [
    {
      title: "Data e hora", dataIndex: "data", key: "data", width: 140,
      render: (text) => (
        <Space direction="vertical" size={0}>
          <Text strong>{dayjs(text).format("DD/MM/YYYY")}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(text).format("HH:mm")}</Text>
        </Space>
      ),
    },
    { title: "Fila", dataIndex: "fila", key: "fila", width: 120, render: (fila) => <Tag color="geekblue">{fila}</Tag> },
    ...(!isMobile ? [
      { title: "Espera", dataIndex: "espera", key: "espera", width: 80, align: "center" },
      { title: "Duração", dataIndex: "duracao", key: "duracao", width: 80, align: "center" },
    ] : []),
    {
      title: "Status", dataIndex: "status", key: "status", width: 140,
      render: (status) => {
        const statusConfig = {
          Completada: { color: "#52c41a", icon: <CheckCircleOutlined /> },
          "Sem resposta": { color: "#ff4d4f", icon: <CloseCircleOutlined /> },
          Abandonada: { color: "#fa8c16", icon: <ExclamationCircleOutlined /> },
        };
        const config = statusConfig[status] || { color: "#d9d9d9", icon: <InfoCircleOutlined /> };
        return (
          <Tag color={config.color} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: isMobile ? 11 : 13, border: "none" }}>
            {config.icon}{status}
          </Tag>
        );
      },
    },
    {
      title: "Operador", dataIndex: "agente", key: "agente", width: 130,
      render: (agente) => (
        <Space>
          <Avatar size="small" icon={<PhoneOutlined />} style={{ backgroundColor: "#1890ff" }} />
          <Text>{agente}</Text>
        </Space>
      ),
    },
  ];

  const opcoesCheckbox = [
    { label: "Completada", value: "Completada" },
    { label: "Sem resposta", value: "Sem_Resposta" },
    { label: "Abandonada", value: "Abandonada" },
  ];

  const p = isMobile ? "12px 8px" : isTablet ? "20px 16px" : "32px 48px";

  return (
    <div style={{ padding: p, minHeight: "100vh", overflowY: "auto", boxSizing: "border-box", background: "linear-gradient(145deg, #f0f2f5 0%, #f8f9fa 100%)" }}>

      {/* Cabeçalho */}
      <div style={{ marginBottom: 24, padding: isMobile ? 16 : "24px 32px", background: "linear-gradient(135deg, rgba(24,144,255,0.08) 0%, rgba(24,144,255,0.02) 100%)", borderRadius: 20, boxShadow: "0 4px 12px rgba(0,0,0,0.03)", border: "1px solid rgba(24,144,255,0.1)" }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} xl={8}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Avatar size={isMobile ? 48 : 64} icon={<TeamOutlined />} style={{ backgroundColor: "#1890ff", boxShadow: "0 4px 12px rgba(24,144,255,0.3)", flexShrink: 0 }} />
              <div>
                <Title level={isMobile ? 4 : 2} style={{ margin: 0, color: "#1a3353" }}>Gestão de Chamadas</Title>
                {!isMobile && <Text type="secondary" style={{ fontSize: 16 }}>Acompanhe todas as chamadas recebidas e seu status</Text>}
              </div>
            </div>
          </Col>
          <Col xs={24} xl={16}>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12} md={6}>
                <Text strong style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Período</Text>
                <RangePicker format="DD/MM/YYYY" value={datas} onChange={(values) => setDatas(values)} style={{ borderRadius: 10, width: "100%" }} size={isMobile ? "middle" : "large"} />
              </Col>
              <Col xs={12} sm={6} md={4}>
                <Text strong style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Status</Text>
                <Select placeholder="Status" allowClear onChange={(value) => setStatusFiltro(value)} style={{ width: "100%" }} size={isMobile ? "middle" : "large"}>
                  <Option value="Completada">Completada</Option>
                  <Option value="Sem Resposta">Sem resposta</Option>
                  <Option value="Abandonada">Abandonada</Option>
                </Select>
              </Col>
              <Col xs={12} sm={6} md={4}>
                <Text strong style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Fila</Text>
                <Select placeholder="Fila" value={filaFiltro} onChange={(value) => setFilaFiltro(value)} allowClear style={{ width: "100%" }} size={isMobile ? "middle" : "large"}>
                  {filasDisponiveis.map((fila) => <Option key={fila} value={fila}>{fila}</Option>)}
                </Select>
              </Col>
              {perfil === "ADMIN" && (
                <Col xs={12} sm={6} md={4}>
                  <Text strong style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Operador</Text>
                  <Select placeholder="Operador" value={operadorFiltro} onChange={(value) => setOperadorFiltro(value)} allowClear style={{ width: "100%" }} size={isMobile ? "middle" : "large"}>
                    {operadoresDisponiveis.map((op) => <Option key={op} value={op}>{op}</Option>)}
                  </Select>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </div>

      {/* Cards de resumo */}
      <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
        {stats.map((item, index) => (
          <Col xs={12} sm={12} lg={6} key={index}>
            <Tooltip title={item.description} placement="top">
              <Card bordered={false} style={{ borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }} hoverable bodyStyle={{ padding: isMobile ? 14 : 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 16 }}>
                  <Avatar size={isMobile ? 36 : 54} icon={item.icon} style={{ backgroundColor: item.color, boxShadow: `0 4px 12px ${item.color}40`, flexShrink: 0 }} />
                  <Statistic
                    title={<Text type="secondary" style={{ fontSize: isMobile ? 11 : 14 }}>{item.title}</Text>}
                    value={item.value}
                    valueStyle={{ color: "#1a3353", fontWeight: 600, fontSize: isMobile ? 20 : 28 }}
                  />
                </div>
              </Card>
            </Tooltip>
          </Col>
        ))}
      </Row>

      {/* Gráfico de área */}
      <Card style={{ borderRadius: 20, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", marginBottom: 24, overflow: "hidden" }}
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <Space><PhoneOutlined style={{ color: "#1890ff" }} /><span style={{ fontWeight: 600, fontSize: isMobile ? 14 : 16 }}>Chamadas por Dia</span></Space>
            {!isMobile && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Text strong style={{ fontSize: 14, marginRight: 8 }}>Mostrar:</Text>
                <CheckboxGroup options={opcoesCheckbox} value={tiposSelecionados} onChange={setTiposSelecionados} />
              </div>
            )}
          </div>
        }
        bordered={false}
        bodyStyle={{ padding: isMobile ? "12px 4px" : "24px" }}>
        {isMobile && (
          <div style={{ marginBottom: 12, paddingLeft: 8 }}>
            <CheckboxGroup options={opcoesCheckbox} value={tiposSelecionados} onChange={setTiposSelecionados} />
          </div>
        )}
        <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
          <AreaChart data={dadosChart} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
            <defs>
              {Object.entries(areasConfig).map(([key, config]) => (
                <linearGradient key={config.gradientId} id={config.gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={config.color} stopOpacity={0.8} />
                  <stop offset="80%" stopColor={config.color} stopOpacity={0.1} />
                </linearGradient>
              ))}
            </defs>
            <XAxis dataKey="dia" tick={{ fontSize: isMobile ? 10 : 12 }} />
            <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} width={30} />
            <RechartsTooltip content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                const completadas = payload.find((p) => p.dataKey === "Completada")?.value || 0;
                const semResposta = payload.find((p) => p.dataKey === "Sem_Resposta")?.value || 0;
                const abandonadas = payload.find((p) => p.dataKey === "Abandonada")?.value || 0;
                const total = payload[0]?.payload?.total || 0;
                return (
                  <div style={{ backgroundColor: "#fff", border: "1px solid #ccc", borderRadius: 10, padding: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: isMobile ? 12 : 14 }}>
                    <p style={{ margin: 0, fontWeight: "bold" }}>{label}</p>
                    <p style={{ color: "#52c41a", margin: 0 }}>Completada: {completadas}</p>
                    <p style={{ color: "#ff4d4f", margin: 0 }}>Sem Resposta: {semResposta}</p>
                    <p style={{ color: "#fa8c16", margin: 0 }}>Abandonada: {abandonadas}</p>
                    <hr style={{ margin: "6px 0" }} />
                    <p style={{ margin: 0, fontWeight: "bold", color: "#000" }}>Total: {total}</p>
                  </div>
                );
              }
              return null;
            }} />
            <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 13 }} />
            {tiposSelecionados.includes("Completada") && (
              <Area type="linear" dataKey="Completada" stroke={areasConfig.Completada.stroke} strokeWidth={2} fill={`url(#${areasConfig.Completada.gradientId})`} dot={{ r: isMobile ? 2 : 4, stroke: areasConfig.Completada.stroke, strokeWidth: 1, fill: "#fff" }} activeDot={{ r: 6 }} name="Completada" />
            )}
            {tiposSelecionados.includes("Sem_Resposta") && (
              <Area type="linear" dataKey="Sem_Resposta" stroke={areasConfig.Sem_Resposta.stroke} strokeWidth={2} fill={`url(#${areasConfig.Sem_Resposta.gradientId})`} dot={{ r: isMobile ? 2 : 4, stroke: areasConfig.Sem_Resposta.stroke, strokeWidth: 1, fill: "#fff" }} activeDot={{ r: 6 }} name="Sem resposta" />
            )}
            {tiposSelecionados.includes("Abandonada") && (
              <Area type="linear" dataKey="Abandonada" stroke={areasConfig.Abandonada.stroke} strokeWidth={2} fill={`url(#${areasConfig.Abandonada.gradientId})`} dot={{ r: isMobile ? 2 : 4, stroke: areasConfig.Abandonada.stroke, strokeWidth: 1, fill: "#fff" }} activeDot={{ r: 6 }} name="Abandonada" />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {/* Gráfico de barras por operador — só ADMIN */}
      {perfil === "ADMIN" && (
        <Card style={{ borderRadius: 20, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", marginBottom: 24, overflow: "hidden" }}
          title={<Space><TeamOutlined style={{ color: "#1890ff" }} /><span style={{ fontWeight: 600, fontSize: isMobile ? 14 : 16 }}>Chamadas atendidas por operador</span></Space>}
          bordered={false}
          bodyStyle={{ padding: isMobile ? "12px 4px" : "24px" }}>
          <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
            <BarChart data={dadosAgente} margin={{ top: 20, right: 10, left: 0, bottom: 5 }}>
              <XAxis dataKey="agente" tick={{ fontSize: isMobile ? 10 : 12 }} />
              <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} width={30} />
              <RechartsTooltip contentStyle={{ backgroundColor: "#fff", borderRadius: 10, border: "1px solid #d9d9d9", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
              <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 13 }} />
              <Bar dataKey="total_atendidas" fill="#1890ff" name="Chamadas atendidas" barSize={isMobile ? 20 : 40} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Tabela de Chamadas */}
      <Card bordered={false} style={{ borderRadius: 20, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", overflow: "hidden" }} bodyStyle={{ padding: 0 }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <Title level={4} style={{ margin: 0, fontSize: isMobile ? 15 : 18 }}>Registo de chamadas</Title>
            <Space size="small" wrap>
              <Tag color="blue" style={{ fontSize: 13, padding: "4px 12px", borderRadius: 20 }}>{filteredChamadas.length} chamadas</Tag>
              <Tooltip title="Exportar Excel">
                <Button type="primary" icon={<FaFileCsv />} onClick={irParaGerarExcel} size={isMobile ? "small" : "middle"}
                  style={{ backgroundColor: "#198754", borderColor: "#198754", borderRadius: 10, boxShadow: "0 4px 12px rgba(25,135,84,0.3)" }}>
                  {!isMobile && "Excel"}
                </Button>
              </Tooltip>
              <Tooltip title="Exportar PDF">
                <Button type="primary" icon={<FilePdfOutlined />} onClick={irParaGerarPDF} size={isMobile ? "small" : "middle"}
                  style={{ backgroundColor: "#dc3545", borderColor: "#dc3545", borderRadius: 10, boxShadow: "0 4px 12px rgba(220,53,69,0.3)" }}>
                  {!isMobile && "PDF"}
                </Button>
              </Tooltip>
            </Space>
          </div>
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredChamadas}
          loading={loading}
          size={isMobile ? "small" : "middle"}
          scroll={{ x: "max-content" }}
          pagination={{
            pageSize: 5,
            size: isMobile ? "small" : "default",
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            position: ["bottomCenter"],
            showTotal: (total, range) => isMobile ? `${range[0]}-${range[1]} / ${total}` : `${range[0]}-${range[1]} de ${total} chamadas`,
            style: { margin: "16px 0", paddingBottom: 8 },
          }}
          rowClassName={() => "custom-row-hover"}
        />
      </Card>

      <style>{`
        .custom-row-hover:hover { background-color: #f0f7ff !important; transition: background-color 0.2s; }
        @media (max-width: 767px) { .ant-table-pagination { justify-content: center !important; } }
      `}</style>
    </div>
  );
};

export default ListagemChamadas;